<template>
  <div class="canvas-ai-root">
    <transition name="canvas-ai-drawer">
      <div
        v-show="panelVisible"
        class="canvas-ai-drawer"
        @mousedown.stop
        @click.stop
        @keydown.stop="onPanelKeydown"
        @keyup.stop="onPanelKeyup"
      >
        <button
          type="button"
          class="canvas-ai-collapse"
          :title="$tt('收起 (Alt+3)')"
          @click="closePanel"
        >
          <i class="el-icon-d-arrow-right" />
        </button>
        <div class="canvas-ai-header">
          <span>{{ $tt("智能画布助手") }}</span>
          <div class="canvas-ai-header-actions">
            <el-tag v-if="currentModel" size="mini">{{ currentModel }}</el-tag>
            <el-tag v-if="previewActive" size="mini" type="warning">{{ $tt("预览中") }}</el-tag>
            <span class="canvas-ai-hotkey">Alt+3</span>
            <i class="el-icon-close" :title="$tt('收起 (Alt+3)')" @click="closePanel" />
          </div>
        </div>

        <div
          v-if="showTargetHint"
          class="canvas-ai-target"
          :class="{ 'is-empty': !liveTargetNames.length && !refChips.length }"
        >
          {{ liveTargetLabel }}
        </div>

        <div ref="messageList" class="canvas-ai-messages">
          <div
            v-for="(msg, idx) in messages"
            :key="idx"
            class="canvas-ai-msg"
            :class="'is-' + msg.role"
          >
            <div v-if="msg.refs && msg.refs.length" class="canvas-ai-msg-refs">
              <span
                v-for="chip in msg.refs"
                :key="'m-' + chip.id"
                class="canvas-ai-chip is-static"
              >
                <span class="chip-icon" :style="{ background: chip.color }">{{ chip.short }}</span>
                <span class="chip-name">{{ chip.name }}</span>
              </span>
            </div>
            <div class="canvas-ai-msg-bubble">{{ msg.content }}</div>
            <div v-if="msg.images && msg.images.length" class="canvas-ai-msg-images">
              <div v-for="(img, i) in msg.images" :key="i" class="canvas-ai-img-item">
                <img :src="img.dataUrl" :alt="img.fileName" />
                <span>{{ $tt(img.pressed ? '按压态' : '普通态') }}</span>
              </div>
            </div>
          </div>
          <div v-if="loading" class="canvas-ai-msg is-assistant">
            <div class="canvas-ai-msg-bubble">{{ $tt("思考中...") }}</div>
          </div>
        </div>

        <div v-if="pendingPlan" class="canvas-ai-preview-bar">
          <p class="canvas-ai-summary">{{ $tt(pendingPlan.summary || '已生成操作计划') }}</p>
          <ul v-if="pendingPlan.steps && pendingPlan.steps.length" class="canvas-ai-steps">
            <li v-for="(s, i) in pendingPlan.steps" :key="i">{{ (s.tool || s.name) }}</li>
          </ul>
        </div>

        <div class="canvas-ai-composer">
          <div v-if="refChips.length" class="canvas-ai-chips">
            <span
              v-for="chip in refChips"
              :key="chip.id"
              class="canvas-ai-chip"
            >
              <span class="chip-icon" :style="{ background: chip.color }">{{ chip.short }}</span>
              <span class="chip-name">{{ chip.name }}</span>
              <i class="el-icon-close chip-remove" @click.stop="removeChip(chip.id)" />
              <span class="chip-tip">{{ chip.tooltip }}</span>
            </span>
          </div>
          <el-input
            ref="chatInput"
            v-model="inputText"
            type="textarea"
            :rows="3"
            :disabled="!!pendingPlan || loading"
            :placeholder="$tt('右键组件「加入AI对话」，或先点选再描述修改，例如：名字改成123，字改红色')"
            @keydown.native="onChatKeydown"
            @keyup.native.stop
          />
          <div class="canvas-ai-input-actions">
            <template v-if="pendingPlan">
              <el-button size="mini" :disabled="previewActive || loading" @click="previewPlan">
                {{ $tt("预览") }}
              </el-button>
              <el-button size="mini" type="primary" :loading="loading" @click="applyPlan">
                {{ $tt("应用") }}
              </el-button>
              <el-button size="mini" :disabled="loading" @click="revertTurn">
                {{ $tt("撤销") }}
              </el-button>
            </template>
            <el-button
              v-else
              type="primary"
              size="mini"
              :loading="loading"
              @click="sendMessage"
            >
              {{ $tt("发送") }}
            </el-button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  createCanvasAiContext,
  chatAndPlan,
  runPlan,
} from '@/canvas-ai';
import { collectLiveTargets, describeTargets, buildRefChip } from '@/canvas-ai/context';
import eventBus from '@/utils/eventBus';

const FAB_SIZE = 48;
const SNAP = 12;
const STORAGE_KEY = 'canvas_ai_fab_pos';

export default {
  name: 'CanvasAiPanel',
  props: {
    editorHooks: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      panelVisible: false,
      fabPos: { x: null, y: null },
      snapEdge: 'right',
      dragging: false,
      dragMoved: false,
      dragOffset: { x: 0, y: 0 },
      messages: [
        {
          role: 'assistant',
          content: '你好，先点选或框选组件再描述修改。发送后先看回答，再预览、应用或撤销。应用会走和顶部一样的保存。',
        },
      ],
      inputText: '',
      loading: false,
      pendingPlan: null,
      previewActive: false,
      appliedThisTurn: false,
      snapshotPushed: false,
      aiCtx: null,
      currentModel: '',
      pinnedAreaIds: [],
      refChips: [],
      localSnapStack: [],
      hintDismissed: false,
    };
  },
  computed: {
    fabStyle() {
      const x = this.fabPos.x != null ? this.fabPos.x : window.innerWidth - FAB_SIZE - 24;
      const y = this.fabPos.y != null ? this.fabPos.y : window.innerHeight - FAB_SIZE - 120;
      return { left: `${x}px`, top: `${y}px` };
    },
    panelStyle() {
      const fabX = this.fabPos.x != null ? this.fabPos.x : window.innerWidth - FAB_SIZE - 24;
      if (fabX < window.innerWidth / 2) {
        return { left: '16px', right: 'auto' };
      }
      return { right: '16px', left: 'auto' };
    },
    liveTargetNames() {
      if (this.refChips.length) {
        return this.refChips.map((c) => `${c.short}「${c.name}」`);
      }
      return describeTargets(collectLiveTargets(this.$store, this.pinnedAreaIds));
    },
    liveTargetLabel() {
      if (this.refChips.length) {
        return `对话引用（${this.refChips.length}）：已加入输入框，发送时会改这些组件`;
      }
      if (!this.liveTargetNames.length) {
        return '未引用组件：右键「加入AI对话」，或先在画布上点选/框选';
      }
      return `当前画布选中（${this.liveTargetNames.length}）：${this.liveTargetNames.join('、')}`;
    },
    showTargetHint() {
      if (this.refChips.length) {
        return true;
      }
      return !this.hintDismissed;
    },
  },
  mounted() {
    this.restoreFabPos();
    this.aiCtx = createCanvasAiContext({
      store: this.$store,
      request: this.$request,
      hooks: this.editorHooks,
    });
    document.addEventListener('mousemove', this.onDocMouseMove);
    document.addEventListener('mouseup', this.onDocMouseUp);
    window.addEventListener('resize', this.clampFabPos);
    eventBus.$on('canvasAiTarget', this.onCanvasAiTarget);
  },
  beforeDestroy() {
    eventBus.$off('canvasAiTarget', this.onCanvasAiTarget);
    document.removeEventListener('mousemove', this.onDocMouseMove);
    document.removeEventListener('mouseup', this.onDocMouseUp);
    window.removeEventListener('resize', this.clampFabPos);
    if (this.previewActive && this.aiCtx && this.aiCtx.sandbox) {
      this.aiCtx.sandbox.restore();
    }
  },
  methods: {
    restoreFabPos() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) this.fabPos = JSON.parse(raw);
      } catch (e) { /* ignore */ }
    },
    saveFabPos() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.fabPos));
    },
    clampFabPos() {
      const maxX = window.innerWidth - FAB_SIZE - 8;
      const maxY = window.innerHeight - FAB_SIZE - 8;
      let x = this.fabPos.x != null ? this.fabPos.x : maxX;
      let y = this.fabPos.y != null ? this.fabPos.y : maxY - 80;
      x = Math.max(8, Math.min(maxX, x));
      y = Math.max(8, Math.min(maxY, y));
      this.fabPos = { x, y };
    },
    snapFabToEdge() {
      const x = this.fabPos.x != null ? this.fabPos.x : window.innerWidth - FAB_SIZE - 24;
      const y = this.fabPos.y != null ? this.fabPos.y : window.innerHeight - FAB_SIZE - 120;
      const mid = window.innerWidth / 2;
      const snappedX = x < mid ? SNAP : window.innerWidth - FAB_SIZE - SNAP;
      this.fabPos = { x: snappedX, y };
      this.saveFabPos();
    },
    onFabMouseDown(e) {
      if (e.button !== 0) return;
      this.dragging = true;
      this.dragMoved = false;
      const rect = this.$refs.fab.getBoundingClientRect();
      this.dragOffset = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      e.preventDefault();
    },
    onDocMouseMove(e) {
      if (!this.dragging) return;
      this.dragMoved = true;
      const x = e.clientX - this.dragOffset.x;
      const y = e.clientY - this.dragOffset.y;
      this.fabPos = {
        x: Math.max(8, Math.min(window.innerWidth - FAB_SIZE - 8, x)),
        y: Math.max(8, Math.min(window.innerHeight - FAB_SIZE - 8, y)),
      };
    },
    onDocMouseUp() {
      if (!this.dragging) return;
      this.dragging = false;
      this.snapFabToEdge();
    },
    onFabClick() {
      if (this.dragMoved) return;
      this.panelVisible = !this.panelVisible;
    },
    closePanel() {
      this.panelVisible = false;
    },
    openPanel() {
      if (!this.refChips.length) {
        this.capturePinnedSelection();
      }
      this.syncChipsToPin();
      this.panelVisible = true;
      this.$nextTick(() => {
        this.focusChatInput();
      });
    },
    togglePanel() {
      if (this.panelVisible) {
        this.closePanel();
        return;
      }
      this.openPanel();
    },
    focusChatInput() {
      const el = this.$el && this.$el.querySelector('.canvas-ai-drawer textarea');
      if (el && el.focus) {
        el.focus();
      }
    },
    onChatKeydown(e) {
      e.stopPropagation();
      eventBus.$emit('canvasAiTyping');
      if (this.isAltToggle(e)) {
        e.preventDefault();
        this.closePanel();
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        this.sendMessage();
      }
    },
    onPanelKeydown(e) {
      eventBus.$emit('canvasAiTyping');
      if (this.isAltToggle(e)) {
        e.preventDefault();
        this.closePanel();
      }
    },
    onPanelKeyup() {
      eventBus.$emit('canvasAiTyping');
    },
    isAltToggle(e) {
      return !!(
        e &&
        e.altKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.shiftKey &&
        (e.code === 'Digit3' || e.code === 'Numpad3' || e.key === '3')
      );
    },
    addComponentsToChat(components) {
      this.hintDismissed = false;
      const list = (components || []).filter(
        (c) => c && c.id != null && String(c.id) !== 'Back'
      );
      list.forEach((c) => {
        const chip = buildRefChip(c);
        if (!chip) {
          return;
        }
        const exists = this.refChips.some((x) => String(x.id) === String(chip.id));
        if (!exists) {
          this.refChips.push(chip);
        }
      });
      this.syncChipsToPin();
      this.openPanel();
    },
    removeChip(id) {
      this.refChips = this.refChips.filter((c) => String(c.id) !== String(id));
      this.syncChipsToPin();
    },
    syncChipsToPin() {
      if (this.refChips.length) {
        this.pinnedAreaIds = this.refChips.map((c) => c.id);
      }
      if (this.aiCtx) this.aiCtx.pinnedAreaIds = this.pinnedAreaIds.slice();
    },
    onCanvasAiTarget(payload) {
      if (this.loading || this.previewActive) return;
      this.hintDismissed = false;
      if (payload && Array.isArray(payload.ids) && payload.ids.length) {
        this.pinnedAreaIds = payload.ids.slice();
      } else if (payload && payload.id != null && String(payload.id) !== 'Back') {
        this.pinnedAreaIds = [payload.id];
      } else {
        this.$nextTick(() => {
          this.capturePinnedSelection();
          if (this.aiCtx) this.aiCtx.pinnedAreaIds = this.pinnedAreaIds.slice();
        });
        return;
      }
      if (this.aiCtx) this.aiCtx.pinnedAreaIds = this.pinnedAreaIds.slice();
    },
    capturePinnedSelection() {
      const state = this.$store.state;
      const areaIds = ((state.areaData && state.areaData.components) || [])
        .filter((c) => c && c.id != null && String(c.id) !== 'Back')
        .map((c) => c.id);
      if (areaIds.length) {
        const resolved = collectLiveTargets(this.$store, areaIds);
        this.pinnedAreaIds = resolved.length ? resolved.map((c) => c.id) : areaIds.slice();
        return;
      }
      const cur = state.curComponent;
      if (cur && cur.id != null && String(cur.id) !== 'Back') {
        const resolved = collectLiveTargets(this.$store, [cur.id]);
        this.pinnedAreaIds = resolved.length ? [resolved[0].id] : [cur.id];
      }
    },
    lockSelectionForTurn() {
      if (this.refChips.length) {
        this.syncChipsToPin();
        return;
      }
      this.capturePinnedSelection();
      if (this.aiCtx) this.aiCtx.pinnedAreaIds = this.pinnedAreaIds.slice();
    },
    preparePlanTargets() {
      this.lockSelectionForTurn();
      const fromPlan = this.pendingPlan && this.pendingPlan.targetIds;
      if ((!this.pinnedAreaIds || !this.pinnedAreaIds.length) && fromPlan && fromPlan.length) {
        this.pinnedAreaIds = fromPlan.slice();
      }
      if (this.aiCtx) this.aiCtx.pinnedAreaIds = this.pinnedAreaIds.slice();
    },
    clearSelectionHint() {
      this.refChips = [];
      this.pinnedAreaIds = [];
      this.hintDismissed = true;
      if (this.aiCtx) this.aiCtx.pinnedAreaIds = [];
    },
    restorePinnedArea() {
      if (this.editorHooks && this.editorHooks.restoreAreaVisual && this.pinnedAreaIds.length > 1) {
        this.editorHooks.restoreAreaVisual(this.pinnedAreaIds);
      }
    },
    scrollMessages() {
      this.$nextTick(() => {
        const el = this.$refs.messageList;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    unwrapInner(res) {
      const payload = res && res.data;
      return payload && payload.data !== undefined ? payload.data : payload;
    },
    snapshotMeta() {
      if (this.editorHooks && this.editorHooks.getSnapshotMeta) {
        return this.editorHooks.getSnapshotMeta() || {};
      }
      return {};
    },
    currentPageJson() {
      if (this.editorHooks && this.editorHooks.getPageSnapshot) {
        return this.editorHooks.getPageSnapshot();
      }
      try {
        return JSON.parse(JSON.stringify(this.$store.state.componentData || []));
      } catch (e) {
        return this.$store.state.componentData || [];
      }
    },
    async pushTurnSnapshot() {
      const pageData = this.currentPageJson();
      const meta = this.snapshotMeta();
      if (!meta.projectId || !this.$request.canvasAiSnapshotPush) {
        this.localSnapStack.unshift(pageData);
        if (this.localSnapStack.length > 20) {
          this.localSnapStack.length = 20;
        }
        this.snapshotPushed = true;
        return;
      }
      const res = await this.$request.canvasAiSnapshotPush({
        projectId: meta.projectId,
        pageId: meta.pageId,
        pageData,
      });
      if (res && res.status && res.status !== 'success') {
        throw new Error(res.msg || '对话前缓存失败');
      }
      this.snapshotPushed = true;
    },
    async popTurnSnapshot() {
      const meta = this.snapshotMeta();
      if (!meta.projectId || !this.$request.canvasAiSnapshotPop) {
        this.snapshotPushed = false;
        return this.localSnapStack.length ? this.localSnapStack.shift() : null;
      }
      const res = await this.$request.canvasAiSnapshotPop({
        projectId: meta.projectId,
        pageId: meta.pageId,
      });
      if (res && res.status && res.status !== 'success') {
        throw new Error(res.msg || '读取撤销缓存失败');
      }
      this.snapshotPushed = false;
      const inner = this.unwrapInner(res);
      return inner && inner.pageData ? inner.pageData : null;
    },
    async saveWithExistingLogic() {
      if (this.editorHooks && this.editorHooks.saveProject) {
        return this.editorHooks.saveProject();
      }
      return false;
    },
    resetTurnFlags() {
      this.pendingPlan = null;
      this.previewActive = false;
      this.appliedThisTurn = false;
      if (this.aiCtx) this.aiCtx.previewMode = false;
    },
    async sendMessage() {
      const text = (this.inputText || '').trim();
      if (!text || this.loading || this.pendingPlan) return;
      this.lockSelectionForTurn();
      if (this.refChips.length) {
        this.pinnedAreaIds = this.refChips.map((c) => c.id);
        if (this.aiCtx) this.aiCtx.pinnedAreaIds = this.pinnedAreaIds.slice();
      }
      let userText = text;
      if (this.pinnedAreaIds.length) {
        userText = `【当前框选组件 id：${this.pinnedAreaIds.map((id) => String(id)).join(',')}】\n${text}`;
      }
      this.messages.push({
        role: 'user',
        content: text,
        refs: this.refChips.slice(),
      });
      this.inputText = '';
      this.loading = true;
      this.scrollMessages();
      let pushed = false;
      try {
        await this.pushTurnSnapshot();
        pushed = true;
        const chatResult = await chatAndPlan(
          userText,
          this.aiCtx,
          async ({ messages }) => {
            const res = await this.$request.canvasAiChat({ messages });
            if (res && res.status && res.status !== 'success') {
              throw new Error(res.msg || 'AI 对话失败');
            }
            const inner = this.unwrapInner(res);
            if (inner && inner.model) {
              this.currentModel = inner.model;
            }
            const payload = res && res.data;
            const content =
              (inner && (inner.content || inner.message)) ||
              (typeof payload === 'string' ? payload : '');
            return { content, plan: inner && inner.plan, model: inner && inner.model };
          }
        );

        if (!chatResult.ok) {
          if (pushed) {
            try { await this.popTurnSnapshot(); } catch (e) { /* ignore */ }
          }
          const failMsg = chatResult.message || '模型返回的 JSON 格式不正确';
          this.$message.error(failMsg);
          this.messages.push({
            role: 'assistant',
            content: `失败：${failMsg}`,
          });
          return;
        }

        this.pendingPlan = chatResult.plan;
        this.pendingPlan.targetIds = this.pinnedAreaIds.slice();
        this.appliedThisTurn = false;
        this.previewActive = false;
        this.messages.push({
          role: 'assistant',
          content: `${chatResult.plan.summary || '已生成操作计划'}。可点「预览」看效果，「应用」按原保存逻辑写入，「撤销」回到这次对话前。`,
        });
      } catch (err) {
        if (pushed) {
          try { await this.popTurnSnapshot(); } catch (e) { /* ignore */ }
        }
        this.messages.push({
          role: 'assistant',
          content: `请求失败：${(err && err.message) || '请检查 AI 服务是否可用'}`,
        });
      } finally {
        this.loading = false;
        this.scrollMessages();
      }
    },
    async previewPlan() {
      if (!this.pendingPlan || this.previewActive || this.loading) return;
      this.preparePlanTargets();
      this.loading = true;
      try {
        const runResult = await runPlan(this.pendingPlan, this.aiCtx, {
          previewMode: true,
        });
        if (!runResult.ok) {
          if (this.aiCtx.sandbox && this.aiCtx.sandbox.isActive()) {
            this.aiCtx.sandbox.restore();
          }
          this.aiCtx.previewMode = false;
          this.previewActive = false;
          this.messages.push({
            role: 'assistant',
            content: `预览失败：${runResult.message}`,
          });
          return;
        }
        this.previewActive = true;
        this.restorePinnedArea();
        this.messages.push({
          role: 'assistant',
          content: '已在画布上预览，尚未保存。满意再点「应用」，否则点「撤销」。',
        });
      } catch (err) {
        this.messages.push({
          role: 'assistant',
          content: `预览失败：${(err && err.message) || '未知错误'}`,
        });
      } finally {
        this.loading = false;
        this.scrollMessages();
      }
    },
    async applyPlan() {
      if (!this.pendingPlan || this.loading) return;
      this.preparePlanTargets();
      this.loading = true;
      try {
        if (!this.previewActive) {
          const runResult = await runPlan(this.pendingPlan, this.aiCtx, {
            previewMode: true,
          });
          if (!runResult.ok) {
            if (this.aiCtx.sandbox && this.aiCtx.sandbox.isActive()) {
              this.aiCtx.sandbox.restore();
            }
            this.aiCtx.previewMode = false;
            this.messages.push({
              role: 'assistant',
              content: `应用失败：${runResult.message}`,
            });
            return;
          }
          this.previewActive = true;
        }
        if (this.aiCtx.sandbox && this.aiCtx.sandbox.isActive()) {
          this.aiCtx.sandbox.commit();
        }
        this.aiCtx.previewMode = false;
        const saved = await this.saveWithExistingLogic();
        this.appliedThisTurn = true;
        this.previewActive = false;
        this.pendingPlan = null;
        this.clearSelectionHint();
        if (!saved) {
          this.messages.push({
            role: 'assistant',
            content: '画布已改好，这次没有保存成功。请点顶部保存，保存方式和手工编辑相同。',
          });
        } else {
          this.messages.push({
            role: 'assistant',
            content: '已应用。保存走的是顶部同一套逻辑，数据结构没有另写。',
          });
        }
      } catch (err) {
        this.messages.push({
          role: 'assistant',
          content: `应用失败：${(err && err.message) || '未知错误'}`,
        });
      } finally {
        this.loading = false;
        this.scrollMessages();
      }
    },
    async revertTurn() {
      if (this.loading) return;
      this.loading = true;
      try {
        if (this.aiCtx && this.aiCtx.sandbox && this.aiCtx.sandbox.isActive()) {
          this.aiCtx.sandbox.restore();
        }
        let pageData = null;
        if (this.snapshotPushed) {
          pageData = await this.popTurnSnapshot();
        }
        if (pageData && this.editorHooks && this.editorHooks.restorePageSnapshot) {
          this.editorHooks.restorePageSnapshot(pageData);
        }
        if (this.appliedThisTurn) {
          await this.saveWithExistingLogic();
        }
        this.resetTurnFlags();
        this.restorePinnedArea();
        this.messages.push({
          role: 'assistant',
          content: '已撤销到这次对话之前的画布。',
        });
      } catch (err) {
        this.messages.push({
          role: 'assistant',
          content: `撤销失败：${(err && err.message) || '未知错误'}`,
        });
      } finally {
        this.loading = false;
        this.scrollMessages();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.canvas-ai-root {
  position: fixed;
  z-index: 3000;
  pointer-events: none;

  * {
    pointer-events: auto;
  }
}

.canvas-ai-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 480px;
  max-width: 90vw;
  height: 100vh;
  background: #fff;
  border-left: 1px solid #e4e7ed;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.16);
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.canvas-ai-collapse {
  position: absolute;
  left: -18px;
  top: 50%;
  width: 18px;
  height: 64px;
  margin-top: -32px;
  border: 1px solid #dcdfe6;
  border-right: 0;
  border-radius: 8px 0 0 8px;
  background: #fff;
  color: #909399;
  cursor: pointer;
  padding: 0;
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.06);
  &:hover {
    color: #409eff;
    border-color: #409eff;
  }
}

.canvas-ai-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #ebeef5;
  font-weight: 600;
  font-size: 15px;
  flex-shrink: 0;

  .el-icon-close {
    cursor: pointer;
    color: #909399;
    &:hover { color: #409eff; }
  }
}

.canvas-ai-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.canvas-ai-hotkey {
  font-size: 11px;
  color: #909399;
  background: #f4f4f5;
  border-radius: 4px;
  padding: 1px 6px;
  font-weight: 400;
}

.canvas-ai-target {
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.4;
  color: #303133;
  background: #ecf5ff;
  border-bottom: 1px solid #d9ecff;
  word-break: break-all;
  flex-shrink: 0;

  &.is-empty {
    color: #e6a23c;
    background: #fdf6ec;
    border-bottom-color: #faecd8;
  }
}

.canvas-ai-messages {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  min-height: 0;
}

.canvas-ai-msg {
  margin-bottom: 12px;

  &.is-user {
    text-align: right;
    .canvas-ai-msg-bubble {
      background: #409eff;
      color: #fff;
      margin-left: 40px;
    }
    .canvas-ai-msg-refs {
      justify-content: flex-end;
    }
  }

  &.is-assistant .canvas-ai-msg-bubble {
    background: #f4f4f5;
    color: #303133;
    margin-right: 40px;
  }
}

.canvas-ai-msg-refs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}

.canvas-ai-msg-bubble {
  display: inline-block;
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.5;
  text-align: left;
  word-break: break-word;
}

.canvas-ai-msg-images {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.canvas-ai-img-item {
  text-align: center;
  img {
    max-width: 120px;
    max-height: 60px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background: #fafafa;
  }
  span {
    display: block;
    font-size: 11px;
    color: #909399;
    margin-top: 4px;
  }
}

.canvas-ai-preview-bar {
  padding: 10px 12px;
  background: #fdf6ec;
  border-top: 1px solid #faecd8;
}

.canvas-ai-summary {
  margin: 0 0 8px;
  font-size: 12px;
  color: #e6a23c;
}

.canvas-ai-steps {
  margin: 0 0 8px;
  padding-left: 18px;
  font-size: 12px;
  color: #606266;
  max-height: 88px;
  overflow: auto;
}

.canvas-ai-composer {
  padding: 12px 14px 14px;
  border-top: 1px solid #ebeef5;
  background: #fafafa;
  flex-shrink: 0;
  overflow: visible;
}

.canvas-ai-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.canvas-ai-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  height: 24px;
  padding: 0 8px 0 3px;
  border-radius: 6px;
  background: #2f3542;
  color: #7ee0d0;
  font-size: 12px;
  line-height: 1;
  cursor: default;
  vertical-align: middle;

  &.is-static {
    padding-right: 8px;
  }

  .chip-icon {
    flex-shrink: 0;
    min-width: 18px;
    height: 18px;
    padding: 0 3px;
    border-radius: 4px;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .chip-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 160px;
  }

  .chip-remove {
    color: #9aa4b2;
    font-size: 12px;
    cursor: pointer;
    &:hover {
      color: #fff;
    }
  }

  .chip-tip {
    display: none;
    position: absolute;
    left: 0;
    bottom: calc(100% + 8px);
    z-index: 2;
    padding: 6px 10px;
    border-radius: 6px;
    background: #1f2430;
    color: #dbe4ee;
    font-size: 12px;
    white-space: nowrap;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.28);
  }

  &:hover .chip-tip {
    display: block;
  }
}

.canvas-ai-input-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.canvas-ai-drawer-enter-active,
.canvas-ai-drawer-leave-active {
  transition: transform 0.22s ease, opacity 0.22s ease;
}
.canvas-ai-drawer-enter,
.canvas-ai-drawer-leave-to {
  opacity: 0;
  transform: translateX(24px);
}
</style>
